import { useState } from 'react'

import pricingCardsTextNodes from '@data/i18n/pricing-cards.json'

import { lang } from '@lib/constants'
import type { GiftProduct } from '@lib/types/get-started-offer'
import type { SubscriptionBoxSellingPlanData } from '@lib/types'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'

import FreeGiftModal from './FreeGiftModal'

function getPlanDisplayInfo(subscription: SubscriptionBoxSellingPlanData, pageContent: GetStartedPageContent) {
	const numericId = subscription.productId.split('/').pop() || ''
	const bulletPoints =
		// @ts-ignore
		pricingCardsTextNodes?.products?.[numericId] as (string | number)[] | undefined

	const renewalMonths = bulletPoints ? Number(bulletPoints[bulletPoints.length - 1]) : 1

	let displayLabel = pageContent.planLabelMonthly
	if (renewalMonths >= 12) displayLabel = pageContent.planLabelAnnual
	else if (renewalMonths > 1) displayLabel = `${renewalMonths}-Month`

	const currencySymbols: Record<string, string> = { 'en-us': '$', 'en-gb': '£' }
	const currencySymbol = currencySymbols[lang] || '$'
	const priceNum = parseFloat(subscription.priceHeader.replace(/[$£]/g, ''))
	const totalPrice = Math.round(priceNum * renewalMonths)

	const billingDescription =
		renewalMonths > 1
			? pageContent.billingMultimonthTemplate
					.replace('{months}', String(renewalMonths))
					.replace('{currency}', currencySymbol)
					.replace('{total}', String(totalPrice))
			: pageContent.billingMonthly

	return { displayLabel, billingDescription, renewalMonths }
}

export type GiftOffer = {
	products: GiftProduct[]
	valueText: string
	cardLabel?: string | null
	cardSubtext?: string | null
	cardCta?: string | null
	modalHeading?: string | null
	modalSubtext?: string | null
	modalDisclaimer?: string | null
}

export default function MobilePlanCard({
	subscription,
	tag,
	benefits,
	bonus,
	offerCallout,
	couponCode,
	tagSuffix,
	giftOffer,
	isSelected,
	onSelect,
	pageContent: pageContentProp
}: {
	subscription: SubscriptionBoxSellingPlanData
	tag: string | null
	benefits?: string[]
	bonus?: { text: string; subtext?: string } | null
	/** Short description for discount-type offers shown beside the coupon code */
	offerCallout?: string | null
	/** Coupon code to display and copy — renders a styled copy section */
	couponCode?: string | null
	/** Short suffix appended to the tag pill, e.g. "20% off 1st box" */
	tagSuffix?: string | null
	/** Gift product data for free_gift-type offers — renders the gift reveal section */
	giftOffer?: GiftOffer | null
	isSelected: boolean
	onSelect: () => void
	pageContent?: GetStartedPageContent
}) {
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT
	const { displayLabel, billingDescription } = getPlanDisplayInfo(subscription, pageContent)
	const [giftModalOpen, setGiftModalOpen] = useState(false)
	const [copied, setCopied] = useState(false)

	const handleCopyCode = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (!couponCode) return
		navigator.clipboard.writeText(couponCode).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		}).catch(() => {})
	}

	return (
		<>
			{giftOffer && (
				<FreeGiftModal
					isOpen={giftModalOpen}
					onClose={() => setGiftModalOpen(false)}
					products={giftOffer.products}
					totalValue={giftOffer.valueText}
					heading={giftOffer.modalHeading}
					subtext={giftOffer.modalSubtext}
					disclaimer={giftOffer.modalDisclaimer}
				/>
			)}

			<button
				type="button"
				onClick={onSelect}
				className={`relative text-left w-full max-w-[450px] mx-auto transition-all duration-200 border-2 cursor-pointer ${
					isSelected
						? 'border-bbx-style shadow-md'
						: 'border-gray-300 shadow-none hover:border-gray-400 hover:shadow-sm'
				}`}
			>
				{/* Plan name header — filled dark when selected */}
				<div
					className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 px-6 py-3 transition-colors duration-200 ${
						isSelected ? 'bg-bbx-style text-white' : 'bg-white text-bbx-style'
					}`}
				>
					<h3 className="text-2xl font-serif font-medium">{displayLabel}</h3>

					{tag && (
						<span className={`md:ml-auto text-xs font-bold uppercase tracking-wide border px-2 py-0.5 whitespace-nowrap shrink-0 ${
							isSelected ? 'border-white text-white' : 'border-bbx-style text-bbx-style'
						}`}>
							{(() => {
								if (giftOffer) return `${tag} + Free Gift`
								if (tagSuffix) return `${tag} + ${tagSuffix}`
								return tag
							})()}
						</span>
					)}
				</div>

				{/* Plan body — always white */}
				<div className={`bg-white px-6 pt-4 ${giftOffer ? 'pb-0' : 'pb-6'}`}>
					{/* Price */}
					<p className="text-3xl font-bold text-bbx-style">
						{subscription.priceHeader}
						<span className="text-lg font-normal">
							/{subscription.frequencyText === 'month' ? 'Month' : 'Box'}
						</span>
					</p>

					{/* Billing description */}
					<p className="text-sm text-gray-600 mt-1">{billingDescription}</p>

					{/* Checkmark benefits */}
					{benefits && benefits.length > 0 && (
						<ul className="space-y-3 mt-5">
							{benefits.map((benefit, i) => (
								<li
									className="text-sm list-item-checkmark"
									key={i}
									dangerouslySetInnerHTML={{ __html: benefit }}
								/>
							))}
						</ul>
					)}

					{/* Coupon code copy section — discount-type offers */}
					{couponCode && (
						<div className="mt-5 -mx-6 px-5 md:px-7 pt-3 md:pt-4 border-t border-gray-100">
							<div className="flex items-center justify-between gap-3">
								{/* Left: icon + label */}
								<div className="flex items-center gap-2 md:gap-3 min-w-0">
									<div className="shrink-0 w-7 h-7 md:w-9 md:h-9 rounded-full bg-bbx-pearl flex items-center justify-center">
										<span className="text-xs md:text-sm" aria-hidden>%</span>
									</div>
									<p className="text-xs md:text-sm font-semibold text-bbx-style leading-tight">
										{offerCallout ?? '20% off your first box'}
									</p>
								</div>
								{/* Right: code + copy affordance */}
								<button
									type="button"
									onClick={handleCopyCode}
									className="flex flex-col items-end shrink-0 group"
									aria-label={`Copy coupon code ${couponCode}`}
								>
									<p className="text-xs md:text-sm font-bold text-bbx-style leading-tight font-mono tracking-wider border border-dashed border-bbx-style px-2 py-0.5">
										{couponCode}
									</p>
									<p className="text-xs md:text-sm text-gray-600">
										{copied ? 'Copied!' : 'Tap to copy'}
									</p>
								</button>
							</div>
						</div>
					)}

					{/* Free gift reveal section — single touchpoint for gift offers */}
					{giftOffer && (
						<div className="mt-5 -mx-6 px-5 md:px-7 pt-3 md:pt-4 pb-4 md:pb-5 bg-bbx-pearl">
							<div className="flex items-center justify-between gap-3">
								<div className="flex items-center gap-2 md:gap-3 min-w-0">
									<span className="shrink-0 text-2xl md:text-3xl leading-none" aria-hidden>🎁</span>
									<div className="min-w-0">
										<p className="text-xs md:text-sm font-bold text-bbx-style leading-tight">
											{giftOffer.cardLabel ?? 'Free Gift Included'}
										</p>
										<p className="text-sm md:text-base font-bold text-bbx-style">
											{giftOffer.valueText}{giftOffer.cardSubtext ? ` ${giftOffer.cardSubtext}` : ''}
										</p>
									</div>
								</div>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation()
										setGiftModalOpen(true)
									}}
									className="text-xs md:text-sm font-semibold text-bbx-spark underline underline-offset-2 whitespace-nowrap shrink-0"
									aria-label="See what's in your free gift"
								>
									{giftOffer.cardCta ?? "See what's inside →"}
								</button>
							</div>
						</div>
					)}
				</div>
			</button>
		</>
	)
}
